import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, GiftIcon, HelpCircleIcon, QuoteIcon, RepeatIcon, ShieldIcon, SparklesIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, faqs, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
const collapseTransition = {
  duration: 0.25,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-bold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent'}`} data-id="element-16" data-mp="1nqgwlgi6hyew">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-extrabold leading-tight text-slate-900 md:text-4xl" data-id="element-17" data-mp="340t09kk72njq">
      {children}
    </h2>;
}
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border border-violet-100 bg-white p-6 shadow-sm" data-id="element-18" data-mp="16ohjldxu3tbw">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-accent/15" data-id="element-19" data-mp="12qtly34rbmgb" />
      <div className="mb-4 text-sm text-rose" data-id="element-20" data-mp="xpmds2ypcl94">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-21" data-mp="srceph9ux0i8">
        “{item.text}”
      </p>
      <div className="mt-auto flex items-center gap-3 border-t border-violet-100 pt-4" data-id="element-22" data-mp="nzb03op7uxp1">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-accent/20 bg-violet-50 font-display text-sm font-bold text-accent" data-id="element-23" data-mp="j21bgb9niq29">
          {item.initials}
        </span>
        <div data-id="element-24" data-mp="9zs39s1uv8s1">
          <p className="text-sm font-bold text-slate-900" data-id="element-25" data-mp="9zjry4ldsbiy">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-26" data-mp="52jj5lx5cuo5">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-16" data-id="element-27" data-mp="cpjc6awmnxm">
      <Eyebrow data-id="element-28" data-mp="e0ly2sm5pn5">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-29" data-mp="3hlm9eicrfs41">
        Personas reales que ya se mueven <span className="text-rose" data-id="element-30" data-mp="3hmx8w5z6adpc">mejor cada día</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-31" data-mp="3mkflc6cefb23">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-32" data-mp="e4m7y0dd6fp9" />)}
      </div>
    </section>;
}
const pains = ['Te levantás tenso todos los días aunque no hayas hecho nada.', 'Buscás ejercicios en internet, pero no tienen orden ni progresión.', 'Empezás rutinas que abandonás porque no ves resultados.', 'No sabés si lo que hacés es correcto ni cuándo tenés que progresar.'];
const transformations = ['Sin buscar en internet', 'Progresión real semana a semana', 'Sabés qué hacer y cuándo avanzar', 'Un hábito que se sostiene'];
export function PainAndSolution() {
  return <section className="border-y border-violet-100 bg-white px-4 py-12 md:py-16" data-id="element-33" data-mp="2miixvhnbcxlg">
      <div className="mx-auto max-w-4xl" data-id="element-34" data-mp="2hk7n94p72j26">
        <Eyebrow danger data-id="element-35" data-mp="2hj69utkphkha">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-36" data-mp="2w8vgv5fhciph">
          ¿Cuántas veces más vas a levantarte rígido{' '}
          <span className="text-rose" data-id="element-37" data-mp="2raagn6089z11">sin hacer nada al respecto?</span>
        </SectionTitle>
        <p className="mx-auto mt-5 max-w-3xl text-center font-medium text-slate-600" data-id="element-38" data-mp="23kpt09t4qdlf">
          La rigidez no desaparece sola. Y sin un sistema claro, todo queda en intención:
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-39" data-mp="2clo0j22s138r">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-violet-100 bg-violet-50/60 p-4" data-id="element-40" data-mp="28kc8yr0ce7xx">
              <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-41" data-mp="3nzi44jkeqi7l" />
              <span className="text-sm font-medium text-slate-700" data-id="element-42" data-mp="3jyg25uz3vs1x">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-43" data-mp="j633opdpgs5b">
          No es falta de ganas. <span className="text-rose" data-id="element-44" data-mp="n7y3fpfzmxgz">Es falta de sistema.</span>
        </p>

        <div className="mt-12" data-id="element-45" data-mp="s46mhdxb3atf">
          <Eyebrow data-id="element-46" data-mp="s7b2d8k4vuzx">
            <span className="inline-flex items-center gap-2" data-id="element-47" data-mp="x3rqi2rc29d0">
              <SparklesIcon className="h-4 w-4" data-id="element-48" data-mp="x5cgrg6m7652" /> La solución
            </span>
          </Eyebrow>
          <SectionTitle data-id="element-49" data-mp="122w02ui6693v">
            Abrís el tomo, identificás tu nivel y{' '}
            <span className="text-rose" data-id="element-50" data-mp="1636e8ug485tt">en 5 minutos estás listo.</span>
          </SectionTitle>
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-51" data-mp="1awr23tnzvjfa">
            {transformations.map(item => <div key={item} className="rounded-xl border border-accent/20 bg-violet-50/70 p-3 text-center text-xs font-bold text-accent md:text-sm" data-id="element-52" data-mp="1ftr7s3gxmk5v">
                {item}
              </div>)}
          </div>
          <p className="mt-6 text-center font-medium text-slate-600" data-id="element-53" data-mp="3absqwd9rbhf1">
            Eso es el{' '}
            <strong className="text-slate-900" data-id="element-54" data-mp="35amrjwllcvwj">Combo Completo Movilidad y Flexibilidad.</strong>
          </p>
        </div>
      </div>
    </section>;
}
const values = [['5 Tomos completos', 'Listos para usar desde hoy'], ['+200 ejercicios', 'Con técnica paso a paso'], ['12 meses de programa', 'Progresión real incluida'], ['3 niveles', 'Principiante, intermedio y avanzado']] as const;
const beforeAfterRows = [['Ejercicios sueltos sin orden ni progresión', 'Rutina clara y lista desde el primer día'], ['Rutinas que se abandonan a la semana', 'Plan anual que sostiene el hábito'], ['Sin criterios para saber si mejorás', 'Tests mensuales que muestran el progreso'], ['Horas buscando contenido en internet', '5 minutos para saber qué hacer hoy']];
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-16" data-id="element-55" data-mp="253hnf472tfdc">
      <Eyebrow data-id="element-56" data-mp="2549lqms25r7b">
        <span className="inline-flex items-center gap-2" data-id="element-57" data-mp="255u4zg8x9npv">
          <TrophyIcon className="h-4 w-4" data-id="element-58" data-mp="2a19gi1jgj7j4" /> Los 5 tomos
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-59" data-mp="1vegajmvtve3l">
        5 tomos. <span className="text-rose" data-id="element-60" data-mp="1vd54thitnw9u">Todo el cuerpo cubierto.</span>
      </SectionTitle>
      <p className="mt-3 text-center text-sm font-medium text-slate-500" data-id="element-61" data-mp="20bgffugxyat4">
        Tocá un tomo para ver el contenido
      </p>

      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-id="element-62" data-mp="1mf31uwfj2tjx">
        {values.map(([value, detail]) => <div key={value} className="rounded-xl border border-violet-100 bg-white p-4 text-center shadow-sm" data-id="element-63" data-mp="1qc8w8qnizly3">
            <b className="block text-sm leading-tight text-slate-900" data-id="element-64" data-mp="1heygarzrj8mu">{value}</b>
            <span className="text-xs font-medium text-slate-500" data-id="element-65" data-mp="36sbncoz9farn">{detail}</span>
          </div>)}
      </div>

      <div className="mt-8 space-y-3" data-id="element-66" data-mp="3bqvdz2ostaqc">
        {tomos.map((tomo, index) => <div key={tomo.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-200 ${open === index ? 'border-accent shadow-lg shadow-violet-100' : 'border-violet-100'}`} data-id="element-67" data-mp="1041jd8xtmd1l" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-68" data-mp="19vp6wyr9b9xs">
              <span className="flex items-center gap-3" data-id="element-69" data-mp="1dwhl86ezk50x">
                <span className="text-2xl" data-id="element-70" data-mp="1dy2frw1avnys">{tomo.icon}</span>
                <span data-id="element-71" data-mp="1srxggeray6yw">
                  <b className="block text-slate-900" data-id="element-72" data-mp="1ssgydvgycvcx">
                    Tomo {index + 1} — {tomo.name}
                  </b>
                  <small className="font-bold text-accent" data-id="element-73" data-mp="3uklor2iszklj">{tomo.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-74" data-mp="3pm1xqnpcrimb" />
            </button>
            <AnimatePresence initial={false} data-id="element-75" data-mp="38qo1qx88oumb">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} className="overflow-hidden" data-id="element-76" data-mp="3dhml65oiy8ts">
                  <ul className="space-y-2 border-t border-violet-100 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-77" data-mp="2ps245sbxlln5">
                    {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-78" data-mp="2yt9zc1j5i6d0">
                        <span className="text-rose" data-id="element-79" data-mp="2yul2bz4rva32">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>

      <div className="mt-12 grid gap-4 md:grid-cols-2 md:gap-6" data-id="element-80" data-mp="1yk0grx9428fa">
        <div className="rounded-2xl border border-red-200 bg-red-50/40 p-6" data-id="element-81" data-mp="1ujgnx5vklqyy">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-slate-900" data-id="element-82" data-mp="1ulkww28btg63">
            <XIcon className="rounded-lg bg-red-100 p-2 text-red-500" size={36} data-id="element-83" data-mp="1pqx7m15i0hqg" />
            Antes — Sin sistema
          </h3>
          {beforeAfterRows.map(([before]) => <p key={before} className="mb-2.5 flex gap-2 text-sm font-medium text-slate-600" data-id="element-84" data-mp="1fvn8bl6hiogd">
              <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-85" data-mp="1aufvh7phiz2u" />
              {before}
            </p>)}
        </div>
        <div className="rounded-2xl border-2 border-accent bg-white p-6 shadow-lg shadow-violet-100" data-id="element-86" data-mp="16w028m2t8ugz">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-slate-900" data-id="element-87" data-mp="11wvvs379tauc">
            <CheckIcon className="rounded-lg bg-violet-100 p-2 text-accent" size={36} data-id="element-88" data-mp="317ev3br4ba0y" />
            Después — Con el Combo
          </h3>
          {beforeAfterRows.map(([, after]) => <p key={after} className="mb-2.5 flex gap-2 text-sm font-bold text-slate-800" data-id="element-89" data-mp="253tl3kg26o9l">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" data-id="element-90" data-mp="2050abzs5pbd4" />
              {after}
            </p>)}
        </div>
      </div>

      <div className="mt-9 rounded-2xl border-2 border-accent/20 bg-white p-6 text-center shadow-sm" data-id="element-91" data-mp="2e3gm454vls24">
        <p className="text-base font-extrabold text-slate-900 md:text-xl" data-id="element-92" data-mp="1lh5eiaiuqqvt">
          Los 5 tomos, los 5 bonos y el plan anual{' '}
          <span className="text-rose" data-id="element-93" data-mp="1hgd4vg62ijz0">te esperan del otro lado.</span>
        </p>
        <p className="mt-2 text-sm font-medium text-slate-600" data-id="element-94" data-mp="1v6p03nxy8yud">
          Podés postergarlo una semana más… o empezar hoy con todo resuelto.
        </p>
        <div className="mt-5" data-id="element-95" data-mp="1qexgjxd7ntdq">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-96" data-mp="12qnyn6j4bprv">
            DALE, QUIERO EMPEZAR HOY MISMO →
          </Button>
        </div>
      </div>
    </section>;
}
export function Bonuses() {
  return <section className="border-y border-violet-100 bg-white px-4 py-12 md:py-16" data-id="element-97" data-mp="39eo9iq47u080">
      <div className="mx-auto max-w-6xl" data-id="element-98" data-mp="3e66d7lxmiewo">
        <Eyebrow data-id="element-99" data-mp="3e77nho6texdc">
          <span className="inline-flex items-center gap-2" data-id="element-100" data-mp="3e7r9ph3i07x6">
            <GiftIcon className="h-4 w-4" data-id="element-101" data-mp="30fb0fyk07dnt" /> Los 5 bonos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-102" data-mp="35cjnodk0e7vx">
          Además te llevás <span className="text-rose" data-id="element-103" data-mp="35dl0amgob2fr">5 bonos incluidos gratis</span>
        </SectionTitle>
        <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-104" data-mp="3t3ozmhmtwg0y">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-violet-100 bg-white p-5 shadow-sm" data-id="element-105" data-mp="3j4zpjb16bzv9">
              <div className="flex gap-3" data-id="element-106" data-mp="3o5xa1u9np5zx">
                <span className="text-3xl" data-id="element-107" data-mp="27p1dcyap8xpt">{icon}</span>
                <div data-id="element-108" data-mp="27rnm319btj9m">
                  <small className="font-bold uppercase tracking-wide text-accent" data-id="element-109" data-mp="2connuzz57zux">
                    Bono {number}
                  </small>
                  <h3 className="font-bold text-slate-900" data-id="element-110" data-mp="1sl9fytp1473d">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-111" data-mp="1olhdmyq8ax6g">
                {description}
              </p>
              <div className="mt-auto flex border-t border-violet-100 pt-4 text-sm font-bold" data-id="element-112" data-mp="1ev5y0ex6mzjq">
                <span className="text-slate-400 line-through" data-id="element-113" data-mp="19w1plqi315ug">Valor: {value}</span>
                <span className="ml-auto text-rose" data-id="element-114" data-mp="19tyr0i2v64e4">HOY GRATIS</span>
              </div>
            </article>)}
        </div>
        <p className="mx-auto mt-8 max-w-2xl text-center text-lg font-extrabold text-slate-900" data-id="element-115" data-mp="10uv5z8k4zewz">
          Valor total: <s className="font-bold text-slate-400" data-id="element-116" data-mp="vx23oqlyf51z">$60.000 ARS</s> —{' '}
          <span className="text-rose" data-id="element-117" data-mp="2v9e6x6p15acw">hoy incluidos gratis</span> con tu compra.
        </p>
      </div>
    </section>;
}
export function FAQ() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-118" data-mp="2xtu2t7i91i1s">
      <Eyebrow data-id="element-119" data-mp="2xtkctjx7faw3">
        <span className="inline-flex items-center gap-2" data-id="element-120" data-mp="2xrpzors6cn2k">
          <HelpCircleIcon className="h-4 w-4" data-id="element-121" data-mp="32rvps3isq1ij" /> Preguntas frecuentes
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-122" data-mp="37kosklupezrz">
        Lo que más <span className="text-rose" data-id="element-123" data-mp="37lqg3pswpeql">nos preguntan</span>
      </SectionTitle>
      <div className="mt-9 space-y-3" data-id="element-124" data-mp="3chf9fbec1zeg">
        {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-200 ${open === index ? 'border-accent/50 shadow-lg shadow-violet-100' : 'border-violet-100'}`} data-id="element-125" data-mp="2ew9q0ysre9xj" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-126" data-mp="2ovie5b0y0ubz">
              {question}
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-127" data-mp="86d0ccnxowap" />
            </button>
            <AnimatePresence initial={false} data-id="element-128" data-mp="1qmhko9pxxyu">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} className="overflow-hidden" data-id="element-129" data-mp="gjg24o3ul4ad">
                  <div className="border-t border-violet-100 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-130" data-mp="kl9y4j33lk47">
                    {answer}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="border-y border-violet-100 bg-violet-50/70 px-4 py-12 md:py-16" data-id="element-131" data-mp="14c4waobvvxjt">
      <div className="mx-auto max-w-3xl text-center" data-id="element-132" data-mp="1mz5majl6j59l">
        <span className="inline-flex rounded-full border-2 border-accent bg-violet-100 p-5 shadow-lg shadow-violet-100" data-id="element-133" data-mp="1mxkw165we8hj">
          <ShieldIcon className="h-10 w-10 text-accent" data-id="element-134" data-mp="1dyzd39wikrg8" />
        </span>
        <div className="mt-5" data-id="element-135" data-mp="1e123hv1kwmlh">
          <Eyebrow data-id="element-136" data-mp="1ixaohp2gja0k">🛡️ Sin riesgo · 100% garantía</Eyebrow>
        </div>
        <SectionTitle data-id="element-137" data-mp="1izn5o00e5gck">
          Probalo 7 días. Si no te sirve, <span className="text-rose" data-id="element-138" data-mp="1484gpcpk0bh2">te devolvemos el 100%.</span>
        </SectionTitle>
        <div className="mt-6 rounded-2xl border border-violet-100 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-139" data-mp="190o4t3lvap04">
          <p data-id="element-140" data-mp="v650gmmd5bf6">
            Revisás el material.{' '}
            <b className="text-slate-900" data-id="element-141" data-mp="zauus71dihxz">Seguís las rutinas en casa o en el gimnasio.</b> Y si en 7
            días sentís que no era lo que esperabas, escribís a compradigitalyaa@gmail.com y te
            devolvemos el dinero.
          </p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-142" data-mp="qb7q9cdckw0p">
            {['Sin vueltas', 'Sin formularios', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-143" data-mp="2n6so8d20982t">
                <CheckCircle2Icon className="h-5 w-5 text-accent" data-id="element-144" data-mp="2j5h24hicb4zd" />
                {text}
              </span>)}
          </div>
        </div>
        <p className="mt-6 text-lg font-bold text-slate-900" data-id="element-145" data-mp="2e4t4hczlvpw6">
          El riesgo no es tuyo. <span className="text-rose" data-id="element-146" data-mp="3awn0otiewumj">Es nuestro.</span>
        </p>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-id="element-147" data-mp="36vbhp6u1n7nb">
          <Metric value="<1%" label="Devoluciones" data-id="element-148" data-mp="31wi0b3bmywhv" />
          <Metric value="+700" label="Personas satisfechas" data-id="element-149" data-mp="31v71lhx1sexm" />
          <Metric value="7 días" label="Garantía real" data-id="element-150" data-mp="2wxx0v647kguh" />
        </div>
        <p className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-accent" data-id="element-151" data-mp="2wzrm6lje6az3">
          <RepeatIcon className="h-4 w-4" data-id="element-152" data-mp="1qf8l4szjiqmd" /> Empezá hoy y sostenelo todo el año.
        </p>
      </div>
    </section>;
}
function Metric({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="rounded-xl border border-violet-100 bg-white p-3" data-id="element-153" data-mp="1bc76m4mgixw8">
      <p className="font-display text-xl font-bold text-primary" data-id="element-154" data-mp="1gdwghgxy15iq">{value}</p>
      <p className="mt-1 text-[10px] font-bold leading-tight text-slate-500" data-id="element-155" data-mp="1lc7tu1ng5y5p">{label}</p>
    </div>;
}